import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Filter, Search, Calendar, Clock, User, Building2, Phone, FileText, X, Edit, Printer, ChevronDown } from 'lucide-react';
import { appointmentsAPI } from '../../lib/api.js';
import { useCompanies } from '../../hooks/useSharedData.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { formatDate } from '../../utils/formatters.js';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

// Import leave appointment components
import LeaveAppointmentForm from './components/LeaveAppointmentForm.jsx';
import OrderlyRoomForm from './components/OrderlyRoomForm.jsx';
// Import DVBW Surekh font asset so we can reliably embed it in print documents
import dvbwSurekh from '../../assets/fonts/DVBW-TTSurekh Normal.ttf';

export const AppointmentsPage = ({ currentUser }) => {
  // No longer need activeView since we're not using tabs
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    company_id: '',
    request_type: '',
    phone_number: '',
    appointment_date: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });

  // New appointment dropdown state
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [selectedType, setSelectedType] = useState('');

  const openNewAppointmentOfType = (type) => {
    setSelectedType(type);
    setEditingAppointment(null);
    setShowTypeMenu(false);
    setShowSidePane(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
  };

  // Fetch appointments
  const { data: appointments, isLoading } = useQuery({
    queryKey: ['appointments', filters, pagination.page, pagination.limit],
    queryFn: async () => {
      const queryParams = {
        ...filters,
        page: pagination.page || 1,
        limit: pagination.limit || 10,
        sortBy: filters.sortBy || 'created_at',
        sortOrder: filters.sortOrder || 'desc'
      };
      // Debug: log outgoing params
      try { console.debug('[Appointments] Query params', queryParams); } catch (_) {}
      // For type filtering, send a normalized request_type to backend so
      // the server can return properly paginated results for that type.
      // Backend stores some request types with underscores (e.g. 'orderly_room'),
      // so normalize spaces to underscores. If the frontend already sent an
      // underscore variant, keep it as-is.
      if (filters.request_type) {
        queryParams.request_type = String(filters.request_type).toLowerCase().replace(/\s+/g, '_');
      }
      return appointmentsAPI.getAll(queryParams);
    },
    // Removed refetchInterval to prevent unnecessary API calls
    // Data will refetch when filters change or queries are invalidated
    // refetchInterval: 30000,
  });

  // Update pagination state when server response changes
  useEffect(() => {
    if (appointments?.data?.pagination) {
      console.log('=== PAGINATION STATE UPDATE ===');
      console.log('Server pagination:', appointments.data.pagination);
      console.log('Current pagination state:', pagination);
      setPagination(prev => {
        const newState = {
          ...prev,
          ...appointments.data.pagination
        };
        console.log('New pagination state:', newState);
        return newState;
      });
    }
  }, [appointments?.data?.pagination]);

  // Fetch companies
  const { data: companies, isLoading: companiesLoading } = useCompanies();

  // Get appointments data from the response and apply client-side type normalization/filtering
  const normalizeType = (t) => String(t || '').toLowerCase().replace(/\s+/g, '_');
  const rawAppointments = Array.isArray(appointments?.data?.data) ? appointments.data.data : [];
  const normalizedSelectedType = filters?.request_type ? normalizeType(filters.request_type) : '';
  const appointmentsList = normalizedSelectedType
    ? rawAppointments.filter((a) => normalizeType(a.request_type) === normalizedSelectedType)
    : rawAppointments;

  // Debug: log server pagination and rendered count
  useEffect(() => {
    try {
      const payload = appointments?.data || {};
      const paginationInfo = payload.pagination || {
        page: payload.page,
        limit: payload.limit,
        total: payload.total,
        pages: payload.totalPages || payload.pages,
      };
      console.debug('[Appointments] Server pagination', paginationInfo);
      console.debug('[Appointments] Rendered rows', appointmentsList.length);
    } catch (_) {}
  }, [appointments, appointmentsList.length]);

  // Derive pagination summary from API response without overriding local state
  const apiTotal = appointments?.data?.total ?? appointments?.data?.pagination?.total ?? 0;
  const apiPage = appointments?.data?.page ?? appointments?.data?.pagination?.page ?? pagination.page;
  const apiTotalPages = appointments?.data?.totalPages ?? appointments?.data?.pagination?.pages ?? Math.max(1, Math.ceil(apiTotal / (pagination.limit || 10)));

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'pending_clerk':
        return 'warning';
      case 'clerk_processed':
        return 'info';
      case 'completed':
      case 'approved':
        return 'success';
      case 'cancelled':
      case 'rejected':
        return 'danger';
      case 'rescheduled':
        return 'info';
      case 'scheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleCreateLeave = () => {
    setShowLeaveForm(true);
  };

  const handleLeaveFormClose = () => {
    setShowLeaveForm(false);
  };

  const handleLeaveFormSuccess = () => {
    setShowLeaveForm(false);
  };

  const handleEditLeave = (appointment) => {
      setShowLeaveForm(true);
    // Pass the appointment data to LeaveAppointmentForm for editing
      setEditingAppointment(appointment);
  };

  const [showSidePane, setShowSidePane] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setShowSidePane(true);
  };

  const handleCloseSidePane = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setShowSidePane(false);
      setIsModalClosing(false);
    setEditingAppointment(null);
      setSelectedType('');
      setShowTypeMenu(false);
    }, 300); // Match animation duration
  };

  const handlePrintAppointment = (appointment) => {
    try {
    // Helper function to strip HTML tags for plain text display
    const stripHtml = (html) => {
      if (!html) return '';
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };
    
      // Helper function to safely format date
      const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
          return new Date(dateValue).toLocaleDateString();
        } catch (e) {
          return dateValue;
        }
      };
      
      // Helper function to safely get field value
      const safeGet = (obj, field, defaultValue = 'N/A') => {
        const value = obj?.[field];
        return value !== null && value !== undefined ? value : defaultValue;
      };
    
    // Get font URL from the current document's stylesheet (most reliable)
    // If bundler-imported font is available, use it as a reliable fallback in production
    let fontUrl = dvbwSurekh || null;
    try {
      const styleSheets = Array.from(document.styleSheets);
      for (const sheet of styleSheets) {
        try {
          const rules = Array.from(sheet.cssRules || []);
          for (const rule of rules) {
            if (rule instanceof CSSFontFaceRule && rule.fontFamily && rule.fontFamily.includes('DVBW')) {
              // Get the src property
              const src = rule.style.src || rule.src || '';
              // Match url() patterns
              const urlMatch = src.match(/url\(['"]?([^'"]+)['"]?\)/);
              if (urlMatch && urlMatch[1]) {
                let extractedUrl = urlMatch[1];
                // Convert relative URLs to absolute
                if (extractedUrl.startsWith('./')) {
                  extractedUrl = extractedUrl.substring(1); // Remove leading dot
                }
                if (!extractedUrl.startsWith('http') && !extractedUrl.startsWith('//')) {
                  // Make it relative to origin
                  if (extractedUrl.startsWith('/')) {
                    fontUrl = `${window.location.origin}${extractedUrl}`;
                  } else {
                    fontUrl = `${window.location.origin}/${extractedUrl}`;
                  }
                } else {
                  fontUrl = extractedUrl;
                }
                break;
              }
            }
          }
          if (fontUrl) break;
        } catch (e) {
          // Cross-origin stylesheet or other error, skip
          continue;
        }
      }
    } catch (e) {
      // Error accessing stylesheets
      console.warn('Could not extract font URL from stylesheets:', e);
    }
    
    // If we couldn't get the font URL from stylesheets, we already have an imported font
    // (dvbwSurekh) which Vite/webpack will resolve to a public URL — prefer that.
    console.debug('[Print] Using font URL for embedding:', fontUrl);
    
    // If we couldn't get the font URL, we'll skip @font-face and use fallback fonts
    // This prevents font loading errors in production when the font file path is incorrect
      
      // Safely get appointment fields with defaults
      const aptId = safeGet(appointment, 'apt_id', 'N/A');
      const firstName = safeGet(appointment, 'first_name', '');
      const lastName = safeGet(appointment, 'last_name', '');
      const fullName = `${firstName} ${lastName}`.trim() || 'N/A';
      const badgeNumber = safeGet(appointment, 'badge_number', 'N/A');
      const phoneNumber = safeGet(appointment, 'phone_number', 'N/A');
      const requestType = safeGet(appointment, 'request_type', 'N/A');
      const appointmentDate = formatDate(appointment?.appointment_date);
      const appointmentTime = safeGet(appointment, 'appointment_time', '');
      const status = safeGet(appointment, 'status', 'N/A');
      const additionalDetails = appointment?.additional_details || '';
      const clerkRemarks = appointment?.clerk_remarks || '';
      const commandantRemarks = appointment?.commandant_remarks || '';
      const notes = appointment?.notes || '';
      
      // For leave related appointments, also include leave-specific fields
      const isLeaveRelated = requestType?.toLowerCase().includes('leave');
      const leaveType = isLeaveRelated ? (appointment?.leave_type || '') : '';
      const leaveStartDate = isLeaveRelated ? formatDate(appointment?.leave_start_date) : '';
      const leaveEndDate = isLeaveRelated ? formatDate(appointment?.leave_end_date) : '';
      const leaveReason = isLeaveRelated ? (appointment?.leave_reason || '') : '';
      console.log("The marathi data is :: ", leaveReason); 
      
    
    // Print the appointment details with modern design (optimized for single page)
    const printContent = `
      <div style="max-width: 800px; margin: 0 auto; padding: 15px; font-family: 'Roboto', Arial, sans-serif; background: #fff; position: relative; min-height: calc(100vh - 1.6cm); display: flex; flex-direction: column; page-break-inside: avoid;">
        <!-- Watermark - Fixed to page, not content -->
        <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 160px; font-weight: 900; color: rgba(13, 148, 136, 0.12); z-index: 0; pointer-events: none; user-select: none; font-family: 'Roboto', Arial, sans-serif; white-space: nowrap; letter-spacing: 12px; opacity: 1; width: 1000px; height: 200px; display: flex; align-items: center; justify-content: center;">SRPF</div>
        
        <div style="position: relative; z-index: 1; flex: 1; display: flex; flex-direction: column; background: transparent;">
        <!-- Header -->
        <div style="border-bottom: 3px solid #0d9488; padding-bottom: 12px; margin-bottom: 15px;">
          <h1 style="font-family: 'Roboto', Arial, sans-serif; font-size: 22px; font-weight: 700; color: #0f766e; margin: 0 0 5px 0;">Appointment Details</h1>
          <p style="font-family: 'Roboto', Arial, sans-serif; font-size: 12px; color: #64748b; margin: 0;">SRPF Commandant Dashboard</p>
        </div>

        <!-- Appointment ID - Highlighted -->
        <div style="background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%); color: #fff; padding: 10px 15px; border-radius: 6px; margin-bottom: 15px; text-align: center;">
          <div style="font-size: 10px; opacity: 0.9; margin-bottom: 3px;">APPOINTMENT ID</div>
          <div style="font-size: 18px; font-weight: 700; letter-spacing: 1px;">${aptId}</div>
        </div>

        <!-- Basic Information Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 10px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Personal Information</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px;">
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Name</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${fullName}</div>
            </div>
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Badge Number</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${badgeNumber}</div>
            </div>
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Phone</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${phoneNumber}</div>
            </div>
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Status</div>
              <div style="display: inline-block; padding: 2px 8px; background: ${status === 'completed' ? '#dcfce7' : status === 'scheduled' ? '#fef3c7' : '#e0e7ff'}; color: ${status === 'completed' ? '#166534' : status === 'scheduled' ? '#92400e' : '#3730a3'}; border-radius: 15px; font-size: 11px; font-weight: 600; text-transform: capitalize;">${status}</div>
            </div>
          </div>
        </div>

        <!-- Appointment Details Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 10px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Appointment Information</h2>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px;">
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Type</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600; text-transform: capitalize;">${requestType}</div>
            </div>
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Date</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${appointmentDate}</div>
            </div>
            ${appointmentTime ? `
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Time</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${appointmentTime}</div>
            </div>
            ` : ''}
            ${isLeaveRelated && leaveType ? `
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Leave Type</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${leaveType}</div>
            </div>
            ` : ''}
            ${isLeaveRelated && leaveStartDate ? `
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Leave Start Date</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${leaveStartDate}</div>
            </div>
            ` : ''}
            ${isLeaveRelated && leaveEndDate ? `
            <div>
              <div style="font-size: 10px; color: #64748b; font-weight: 500; margin-bottom: 3px; text-transform: uppercase; letter-spacing: 0.5px;">Leave End Date</div>
              <div style="font-size: 13px; color: #1e293b; font-weight: 600;">${leaveEndDate}</div>
            </div>
            ` : ''}
          </div>
        </div>

        ${isLeaveRelated && leaveReason ? `
        <!-- Leave Reason Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 8px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Leave Reason <span style="font-size: 11px; font-weight: 400;">(रजेचे कारण)</span></h2>
          <div style="background: #fff; padding: 10px; border-radius: 4px; border-left: 3px solid #0d9488;">
            <p style="font-family: 'DVBW TT Surekh', 'Noto Sans Devanagari', 'Noto Sans', 'Nirmala UI', 'Mangal', 'Roboto', Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; margin: 0; white-space: pre-wrap;">${leaveReason}</p>
          </div>
        </div>
        ` : ''}

        ${additionalDetails ? `
        <!-- Purpose of Visit Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 8px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Purpose of Visit <span style="font-size: 11px; font-weight: 400;">(भेटीचा उद्देश)</span></h2>
          <div style="background: #fff; padding: 10px; border-radius: 4px; border-left: 3px solid #0d9488;">
            <p style="font-family: 'DVBW TT Surekh', 'Noto Sans Devanagari', 'Noto Sans', 'Nirmala UI', 'Mangal', 'Roboto', Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; margin: 0; white-space: pre-wrap;">${additionalDetails}</p>
          </div>
        </div>
        ` : ''}

        ${clerkRemarks ? `
        <!-- Clerk Remarks Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 8px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Clerk Remarks <span style="font-size: 11px; font-weight: 400;">(क्लर्क टिप्पणी)</span></h2>
          <div style="background: #fff; padding: 10px; border-radius: 4px; border-left: 3px solid #0d9488;">
            <div style="font-family: 'DVBW TT Surekh', 'Noto Sans Devanagari', 'Noto Sans', 'Nirmala UI', 'Mangal', 'Roboto', Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; white-space: pre-wrap;">${stripHtml(clerkRemarks)}</div>
          </div>
        </div>
        ` : ''}

        ${commandantRemarks ? `
        <!-- Commandant's Remarks Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 8px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Commandant's Remarks <span style="font-size: 11px; font-weight: 400;">(कमांडंट टिप्पणी)</span></h2>
          <div style="background: #fff; padding: 10px; border-radius: 4px; border-left: 3px solid #0d9488;">
            <div style="font-family: 'DVBW TT Surekh', 'Noto Sans Devanagari', 'Noto Sans', 'Nirmala UI', 'Mangal', 'Roboto', Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; white-space: pre-wrap;">${stripHtml(commandantRemarks)}</div>
          </div>
        </div>
        ` : ''}

        ${notes ? `
        <!-- Commandant's Order Section -->
        <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; margin-bottom: 12px;">
          <h2 style="font-family: 'Roboto', Arial, sans-serif; font-size: 14px; font-weight: 600; color: #0f766e; margin: 0 0 8px 0; padding-bottom: 6px; border-bottom: 2px solid #0d9488;">Commandant's Order</h2>
          <div style="background: #fff; padding: 10px; border-radius: 4px; border-left: 3px solid #0d9488;">
            <p style="font-family: 'DVBW TT Surekh', 'Noto Sans Devanagari', 'Noto Sans', 'Nirmala UI', 'Mangal', 'Roboto', Arial, sans-serif; font-size: 12px; line-height: 1.5; color: #1e293b; margin: 0; white-space: pre-wrap;">${notes}</p>
          </div>
        </div>
        ` : ''}

        </div>
        
        <!-- Footer - Fixed at bottom -->
        <div style="margin-top: auto; padding-top: 15px; border-top: 2px solid #e2e8f0; text-align: center; position: relative; z-index: 1;">
          <p style="font-family: 'Roboto', Arial, sans-serif; font-size: 10px; color: #64748b; margin: 0;">
            Printed on: ${new Date().toLocaleString()} | SRPF Commandant Dashboard
          </p>
        </div>
        </div>
      </div>
    `;
    
    // Create a hidden iframe for printing (no visible window)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.style.opacity = '0';
    printFrame.style.pointerEvents = 'none';
    document.body.appendChild(printFrame);
    
    // Flag to prevent double printing
    let hasPrinted = false;
    
    const triggerPrint = () => {
      if (hasPrinted) return;
      hasPrinted = true;
      
        try {
      const printWindow = printFrame.contentWindow;
          
          if (!printWindow) {
            console.error('Print window not available');
            return;
          }
      
      // Set suggested filename (browser may use this)
      try {
            printWindow.document.title = `Appointment_${aptId}.pdf`;
      } catch (e) {
        // Ignore if can't set title
      }
      
      // Check if fonts are loaded (only if font URL was found)
      if (fontUrl && printWindow.document.fonts && printWindow.document.fonts.check) {
        // Wait a bit for font to load
        const checkFont = () => {
              try {
          if (printWindow.document.fonts.check('16px "DVBW TT Surekh"')) {
            printWindow.print();
            // Clean up iframe after printing
            setTimeout(() => {
              if (printFrame.parentNode) {
                document.body.removeChild(printFrame);
              }
            }, 1000);
          } else {
            setTimeout(checkFont, 100);
                }
              } catch (e) {
                console.error('Error checking font:', e);
                // Fallback: print anyway
                printWindow.print();
                setTimeout(() => {
                  if (printFrame.parentNode) {
                    document.body.removeChild(printFrame);
                  }
                }, 1000);
          }
        };
        setTimeout(checkFont, 200);
      } else {
        // Fallback: wait a bit then print
        setTimeout(() => {
              try {
          printWindow.print();
          // Clean up iframe after printing
          setTimeout(() => {
            if (printFrame.parentNode) {
              document.body.removeChild(printFrame);
            }
          }, 1000);
              } catch (e) {
                console.error('Error printing:', e);
                // Clean up iframe on error
                if (printFrame.parentNode) {
                  document.body.removeChild(printFrame);
                }
              }
        }, 500);
          }
        } catch (e) {
          console.error('Error in triggerPrint:', e);
          // Clean up iframe on error
          if (printFrame.parentNode) {
            document.body.removeChild(printFrame);
          }
      }
    };
    
    // Write the HTML with font support
      try {
    const printDoc = printFrame.contentWindow.document;
    printDoc.open();
    printDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
              <title>Appointment_${aptId}.pdf</title>
          <style>
            ${fontUrl ? `
            @font-face {
              font-family: 'DVBW TT Surekh';
              src: url('${fontUrl}') format('truetype');
              font-weight: 400;
              font-style: normal;
              font-display: swap;
            }
            ` : ''}
            * {
              box-sizing: border-box;
            }
            body { 
              margin: 0; 
              padding: 0; 
              font-family: 'Roboto', Arial, sans-serif;
              color: #000;
              background: #fff;
            }
            .print-wrapper {
              position: relative;
              min-height: calc(100vh - 1.6cm);
              display: flex;
              flex-direction: column;
            }
            .print-watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 120px;
              font-weight: 900;
              color: rgba(13, 148, 136, 0.08);
              z-index: 0;
              pointer-events: none;
              user-select: none;
              font-family: 'Roboto', Arial, sans-serif;
              white-space: nowrap;
            }
            .print-content-wrapper {
              position: relative;
              z-index: 1;
              flex: 1;
              display: flex;
              flex-direction: column;
            }
            .print-footer {
              margin-top: auto;
              padding-top: 15px;
              border-top: 2px solid #e2e8f0;
              text-align: center;
              position: relative;
              z-index: 1;
            }
            @media print {
              body { 
                margin: 0; 
                padding: 0;
                background: #fff;
              }
              @page {
                margin: 0.8cm;
                size: A4;
              }
              /* Prevent page breaks */
              * {
                page-break-inside: avoid;
                page-break-after: avoid;
              }
              /* Watermark only on first page */
              .print-watermark {
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(-45deg);
              }
            }
            .print-container {
              position: relative;
              min-height: 100vh;
              display: flex;
              flex-direction: column;
            }
            .print-content {
              flex: 1;
              position: relative;
              z-index: 1;
            }
            .print-watermark {
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 120px;
              font-weight: 900;
              color: rgba(13, 148, 136, 0.08);
              z-index: 0;
              pointer-events: none;
              user-select: none;
              font-family: 'Roboto', Arial, sans-serif;
            }
            @media print {
              .print-watermark {
                position: absolute;
              }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    printDoc.close();
    
    // Wait for the iframe to load - use a single event handler
    printFrame.onload = triggerPrint;
    
    // If already loaded, trigger immediately (but only once)
    if (printFrame.contentDocument && printFrame.contentDocument.readyState === 'complete') {
      setTimeout(triggerPrint, 100);
        }
      } catch (e) {
        console.error('Error setting up print document:', e);
        // Clean up iframe on error
        if (printFrame.parentNode) {
          document.body.removeChild(printFrame);
        }
        alert('Error preparing print document. Please try again.');
      }
    } catch (error) {
      console.error('Error in handlePrintAppointment:', error);
      alert('Error printing appointment. Please try again.');
    }
  };

  return (
    <div className="h-full px-6 py-6 overflow-y-auto">
      {/* Header */}
      <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Appointments</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage appointment requests and schedules</p>
        </div>
        <div className="flex items-center gap-2">
          <h2 className="text-gray-600 dark:text-gray-400">All Appointments</h2>
          <Badge variant="secondary" className="text-xs">{pagination.total || appointmentsList.length}</Badge>
          </div>
          <div className="relative flex items-center gap-3">
            <Button onClick={() => setShowTypeMenu((v) => !v)} className="flex items-center gap-2 bg-green-600 hover:bg-green-700">
          <Plus size={16} />
          New Appointment
              <ChevronDown size={14} />
        </Button>
            {showTypeMenu && (
              <div className="absolute right-0 top-12 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg w-56">
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => openNewAppointmentOfType('leave related')}
                >
                  Leave Related
                </button>
                <button
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => openNewAppointmentOfType('orderly room')}
                >
                  Orderly Room
                </button>
              </div>
            )}
          </div>
      </div>

      </div>

      {/* Main Content */}
      <div className="space-y-6">
      {/* Appointment Type Tabs */}
      <div className="w-full">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-6">
            <button
              type="button"
              onClick={() => handleFilterChange('request_type', '')}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                !filters.request_type
                  ? 'border-teal-700 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange('request_type', 'leave related')}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                (filters.request_type === 'leave related' || filters.request_type === 'leave_related')
                  ? 'border-teal-700 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leave Related
            </button>
            <button
              type="button"
              onClick={() => handleFilterChange('request_type', 'orderly room')}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                (filters.request_type === 'orderly room' || filters.request_type === 'orderly_room')
                  ? 'border-teal-700 text-teal-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Orderly Room
            </button>
          </nav>
        </div>
      </div>

      {/* Quick Filters */}
      <Card className="my-6">
            <div className="flex flex-wrap gap-4 items-center p-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
                placeholder="Search by name, badge number, or reference..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 pr-4 py-2 w-full"
          />
            </div>
          </div>

          <div className="flex items-center gap-2">
          <Input
              placeholder="Phone number..."
            value={filters.phone_number}
            onChange={(e) => handleFilterChange('phone_number', e.target.value)}
            icon={<Phone size={16} />}
              className="w-40"
          />

          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="clerk_processed">Clerk Processed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rescheduled">Rescheduled</option>
          </select>

          <Input
            type="date"
            value={filters.appointment_date}
            onChange={(e) => handleFilterChange('appointment_date', e.target.value)}
            className="px-3 py-2"
            placeholder="Filter by date"
          />

              <Button
            onClick={() => {
              setFilters(prev => ({
                ...prev,
                search: '',
                status: '',
                appointment_date: '',
                phone_number: '',
                sortBy: 'created_at',
                sortOrder: 'desc'
              }));
            }}
            variant="outline"
                size="sm"
            className="flex items-center gap-1"
          >
            <X size={14} />
            Clear
              </Button>
            </div>
          </div>
        </Card>

      {/* Appointments List */}
      <Card>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
                <table className="w-full border border-teal-600 dark:border-teal-700">
              <thead >
                    <tr className="bg-teal-700 text-white dark:bg-teal-800 border-b border-teal-600 dark:border-teal-700 divide-x divide-teal-600 dark:divide-teal-600">
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white"> Appointment ID</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Name</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Badge</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-white dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                    {appointmentsList.map((appointment) => (
                  <tr 
                    key={appointment.apt_id} 
                        className="border-b border-teal-700 dark:border-teal-700 hover:bg-teal-50 dark:hover:bg-teal-800 divide-x divide-teal-600 dark:divide-teal-600"
                      >
                    <td className="py-3 px-4">{appointment.apt_id}</td>
                    <td className="py-3 px-4">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {appointment.first_name} {appointment.last_name}
                      </div>
                          <div className="text-sm text-gray-500">
                            {formatPhoneForDisplay(appointment.phone_number)}
                      </div>
                    </td>
                        <td className="py-3 px-4">{appointment.badge_number}</td>
                    <td className="py-3 px-4">
                          <Badge variant="info">
                        {appointment.request_type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-gray-400" />
                            <span>{formatDate(appointment.appointment_date)}</span>
                        {appointment.appointment_time && (
                              <span className="text-gray-500">
                              {appointment.appointment_time}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={getStatusBadgeVariant(appointment.status)}>
                        {appointment.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                              onClick={() => handleEdit(appointment)}
                          className="p-1 text-blue-600 hover:text-blue-700"
                              title={`Edit ${appointment.request_type === 'leave related' ? 'Leave Application' : 'Appointment'}`}
                        >
                              <Edit size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                              onClick={() => handlePrintAppointment(appointment)}
                              className="p-1 text-green-600 hover:text-green-700"
                              title="Print Appointment"
                        >
                              <Printer size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
                {(!appointmentsList.length) && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 divide-x divide-teal-600 dark:divide-teal-600">
                No appointments found
              </div>
            )}
          </div>
        )}
          {/* Pagination */}
          {(pagination.totalPages > 1 || pagination.total > 0) && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 px-4 pb-4">
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>
                  Showing {((pagination.page || 1) - 1) * (pagination.limit || 10) + 1} to {Math.min((pagination.page || 1) * (pagination.limit || 10), pagination.total || 0)} of {pagination.total || 0} appointments
                </span>
                {pagination.totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      className={`px-3 py-1 rounded border ${pagination.hasPrev ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      onClick={() => {
                        console.log('Previous clicked. Current page:', pagination.page, 'hasPrev:', pagination.hasPrev);
                        setPagination(prev => ({ ...prev, page: (prev.page || 1) - 1 }));
                      }}
                      disabled={!pagination.hasPrev}
                    >
                      Previous
                    </button>
                    <span>
                      Page {pagination.page || 1} of {pagination.totalPages || 0}
                    </span>
                    <button
                      className={`px-3 py-1 rounded border ${pagination.hasNext ? 'hover:bg-gray-100 dark:hover:bg-gray-700' : 'opacity-50 cursor-not-allowed'}`}
                      onClick={() => {
                        console.log('Next clicked. Current page:', pagination.page, 'hasNext:', pagination.hasNext);
                        setPagination(prev => ({ ...prev, page: (prev.page || 1) + 1 }));
                      }}
                      disabled={!pagination.hasNext}
                    >
                      Next
                    </button>
                    <select
                      className="ml-2 border rounded px-2 py-1"
                      value={pagination.limit || 10}
                      onChange={(e) => handlePageSizeChange(e.target.value)}
                    >
                      <option value={10}>10/page</option>
                      <option value={20}>20/page</option>
                      <option value={50}>50/page</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
      </Card>
      </div>


      {/* Sliding Side Pane */}
      {showSidePane && (
        <div 
          className={`fixed inset-0 backdrop-blur-md flex items-center justify-end z-50 transition-all duration-300 ease-out ${
            isModalClosing ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={(e) => {
            // Close pane when clicking on backdrop
            if (e.target === e.currentTarget) {
              handleCloseSidePane();
            }
          }}
        >
          <div 
            className="bg-white dark:bg-gray-900 h-full w-1/2 shadow-2xl dark:shadow-2xl transform transition-all duration-500 ease-out overflow-hidden flex flex-col"
            style={{ 
              transform: isModalClosing ? 'translateX(100%)' : 'translateX(0)',
              animation: showSidePane && !isModalClosing ? 'slideInRight 0.5s ease-out' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50 p-4 shadow-lg z-10">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="p-2">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {editingAppointment ? (
                        editingAppointment.request_type === 'leave related' ? 
                          'Edit Leave Application' : 
                          'Edit Appointment'
                      ) : (
                        'New Appointment'
                      )}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {editingAppointment ? 'Update appointment details' : 'Create a new appointment'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseSidePane}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all duration-200"
                >
                  <X size={20} className="text-gray-500" />
                </Button>
              </div>
            </div>
            
            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {(editingAppointment?.request_type || selectedType) === 'leave related' ? (
                <LeaveAppointmentForm
                  onClose={handleCloseSidePane}
                  onSuccess={() => {
                    handleCloseSidePane();
                    handleLeaveFormSuccess();
                  }}
                  editAppointment={editingAppointment}
                  currentUser={currentUser}
                />
              ) : (
                <OrderlyRoomForm
                  onClose={handleCloseSidePane}
                  onSuccess={() => {
                    handleCloseSidePane();
                    handleLeaveFormSuccess();
                  }}
                  editAppointment={editingAppointment}
                  currentUser={currentUser}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default AppointmentsPage;
